import { useSnapshotFilterBar } from 'features/snapshots/SnapshotFilterBar';
import { ChangeEvent, useEffect, useState } from 'react';
import { Button, Grid, List, ListSubheader } from '@mui/material';

import SnapshotList from '../snapshots/SnapshotList';
import { Snapshot, SnapshotAlarm } from 'types';
import { useGetAnalyzersByModule } from './hooks/useGetAnalyzersByModule';
import { ModuleType } from 'features/analyzerModules/types';
import { useSnapshotAlarmGroups } from 'features/alarms/api/getSnapshotAlarmGroups';
import { useCreateSnapshotAlarmGroup } from 'features/alarms/api/createSnapshotAlarmGroup';
import { useDeleteSnapshotAlarmGroup } from 'features/alarms/api/deleteSnapshotAlarmGroup';

interface Props {
  selectedAlarm: SnapshotAlarm;
}

const AlarmSnapshotList = ({ selectedAlarm }: Props) => {
  const { SnapshotFilterBar, getFilterSnapshotsByName } =
    useSnapshotFilterBar();
  const analyzers = useGetAnalyzersByModule(ModuleType.BASE);
  const { data: alarmGroups } = useSnapshotAlarmGroups({
    alarmId: selectedAlarm?.id || -1,
  });
  const [snapshotsLinkedToAlarm, setSnapshotsLinkedToAlarm] = useState<
    number[]
  >([]);
  const [hasChanges, setHasChanges] = useState(false);

  const createSnapshotAlarmGroupMutation = useCreateSnapshotAlarmGroup({
    alarmId: selectedAlarm?.id || -1,
  });
  const deleteSnapshotAlarmGroupMutation = useDeleteSnapshotAlarmGroup({
    alarmId: selectedAlarm?.id || -1,
  });

  useEffect(() => {
    if (alarmGroups) {
      setSnapshotsLinkedToAlarm(alarmGroups.map((group) => group.snapshot_id));
      setHasChanges(false);
    } else {
      setSnapshotsLinkedToAlarm([]);
      setHasChanges(false);
    }
  }, [alarmGroups]);

  const handleSnapshotChecked = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    if (checked) {
      setSnapshotsLinkedToAlarm((snapshots) => [...snapshots, Number(name)]);
    } else {
      setSnapshotsLinkedToAlarm((snapshots) =>
        snapshots.filter((snapshotId) => snapshotId !== Number(name))
      );
    }
    setHasChanges(true);
  };

  const checkIfSnapshotSelected = (snapshot: Snapshot) => {
    return snapshotsLinkedToAlarm.includes(snapshot.id);
  };

  const handleSubmit = async () => {
    let snapshotIdsToLinkToAlarm = [...snapshotsLinkedToAlarm];
    if (alarmGroups) {
      for (const group of alarmGroups) {
        if (!snapshotsLinkedToAlarm.includes(group.snapshot_id)) {
          deleteSnapshotAlarmGroupMutation.mutateAsync({
            data: {
              alarm_id: selectedAlarm.id,
              snapshot_id: group.snapshot_id,
            },
          });
          snapshotIdsToLinkToAlarm = snapshotIdsToLinkToAlarm.filter(
            (snapshotId) => snapshotId !== group.snapshot_id
          );
        }
      }
    }
    for (const snapshotId of snapshotIdsToLinkToAlarm) {
      createSnapshotAlarmGroupMutation.mutateAsync({
        data: {
          alarm_id: selectedAlarm.id,
          snapshot_id: snapshotId,
        },
      });
    }
    setHasChanges(false);
  };

  return (
    <List
      sx={{ overflow: 'auto', maxHeight: '67vh' }}
      dense
      subheader={
        <ListSubheader component='div' id='nested-list-subheader'>
          <Grid container justifyContent='space-between'>
            <Grid item>Associated Snapshots</Grid>
            <Grid
              item
              container
              xs
              alignItems='center'
              justifyContent='flex-end'
            >
              <Grid item>{SnapshotFilterBar}</Grid>
              <Grid item xs={4}>
                <Button
                  fullWidth
                  variant='contained'
                  disabled={!hasChanges}
                  onClick={handleSubmit}
                >
                  Submit
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </ListSubheader>
      }
    >
      {analyzers &&
        analyzers?.map((analyzer) => (
          <div key={analyzer.id}>
            <ListSubheader
              component='div'
              id='nested-list-subheader'
              disableSticky
            >
              {analyzer.name}
            </ListSubheader>
            <SnapshotList
              analyzerId={analyzer.id}
              checkIfSnapshotSelected={checkIfSnapshotSelected}
              handleSnapshotChecked={handleSnapshotChecked}
              getFilterSnapshots={getFilterSnapshotsByName}
            />
          </div>
        ))}
    </List>
  );
};

export default AlarmSnapshotList;
